import { EMAIL_REGIONS, type EmailRegion } from '../editor-types';
import { normalizeColor } from './selection';
import { TABLE_ROW_CHROME_HOST_ATTRIBUTE } from './table-row-chrome';

export const EMAIL_REGION_ATTRIBUTE = 'data-email-region';

export const EMAIL_REGION_DEFAULT_COLORS: Record<EmailRegion, string> = {
    canvas: '#f0f0f0',
    header: '#4fb8b0',
    card: '#ffffff'
};

export const emailRegionSelector = (region: EmailRegion): string => `[${EMAIL_REGION_ATTRIBUTE}="${region}"]`;

export const queryEmailRegion = (document: Document, region: EmailRegion): HTMLElement | null => {
    return document.querySelector(emailRegionSelector(region));
};

const isEditorOnlyNode = (node: Node): boolean => node instanceof Element && node.hasAttribute(TABLE_ROW_CHROME_HOST_ATTRIBUTE);

const paintBackground = (element: HTMLElement, color: string): void => {
    const fill = color.trim();
    if (!fill) {
        element.removeAttribute('bgcolor');
        element.style.backgroundColor = '';
        return;
    }

    element.setAttribute('bgcolor', fill);
    element.style.backgroundColor = fill;
};

const createPresentationTable = (document: Document, width: string): HTMLTableElement => {
    const table = document.createElement('table');
    table.setAttribute('role', 'presentation');
    table.setAttribute('width', width);
    table.setAttribute('cellpadding', '0');
    table.setAttribute('cellspacing', '0');
    return table;
};

const createHeaderCell = (document: Document): HTMLTableCellElement => {
    const header = document.createElement('td');
    header.setAttribute(EMAIL_REGION_ATTRIBUTE, 'header');
    header.setAttribute('style', `background-color:${EMAIL_REGION_DEFAULT_COLORS.header};padding:20px 24px;color:#ffffff;`);
    header.setAttribute('bgcolor', EMAIL_REGION_DEFAULT_COLORS.header);
    header.appendChild(document.createTextNode('\u00a0'));
    return header;
};

const innerHost = (element: HTMLElement): HTMLElement => {
    if (element.tagName !== 'TABLE') return element;

    const existing = element.querySelector('td');
    if (existing) return existing;

    const row = element.ownerDocument.createElement('tr');
    const cell = element.ownerDocument.createElement('td');
    row.appendChild(cell);
    element.appendChild(row);
    return cell;
};

const movableBodyNodes = (document: Document): Node[] => Array.from(document.body.childNodes).filter((node) => !isEditorOnlyNode(node));

const wrapBodyInSkeleton = (document: Document): void => {
    const content = document.createElement('td');
    content.setAttribute('style', 'padding:24px;');
    movableBodyNodes(document).forEach((node) => content.appendChild(node));

    const card = createPresentationTable(document, '600');
    card.setAttribute(EMAIL_REGION_ATTRIBUTE, 'card');
    card.setAttribute('style', `background-color:${EMAIL_REGION_DEFAULT_COLORS.card};width:600px;`);
    card.setAttribute('bgcolor', EMAIL_REGION_DEFAULT_COLORS.card);

    const headerRow = document.createElement('tr');
    headerRow.appendChild(createHeaderCell(document));
    const contentRow = document.createElement('tr');
    contentRow.appendChild(content);
    card.appendChild(headerRow);
    card.appendChild(contentRow);

    const canvasCell = document.createElement('td');
    canvasCell.setAttribute('align', 'center');
    canvasCell.setAttribute('style', 'padding:24px 0;');
    canvasCell.appendChild(card);

    const canvas = createPresentationTable(document, '100%');
    canvas.setAttribute(EMAIL_REGION_ATTRIBUTE, 'canvas');
    canvas.setAttribute('style', `background-color:${EMAIL_REGION_DEFAULT_COLORS.canvas};width:100%;`);
    canvas.setAttribute('bgcolor', EMAIL_REGION_DEFAULT_COLORS.canvas);

    const canvasRow = document.createElement('tr');
    canvasRow.appendChild(canvasCell);
    canvas.appendChild(canvasRow);
    document.body.appendChild(canvas);
};

const wrapExistingInCanvas = (document: Document): void => {
    if (queryEmailRegion(document, 'canvas')) return;

    const canvasCell = document.createElement('td');
    canvasCell.setAttribute('align', 'center');
    canvasCell.setAttribute('style', 'padding:24px 0;');
    movableBodyNodes(document).forEach((node) => canvasCell.appendChild(node));

    const canvas = createPresentationTable(document, '100%');
    canvas.setAttribute(EMAIL_REGION_ATTRIBUTE, 'canvas');
    canvas.setAttribute('style', `background-color:${EMAIL_REGION_DEFAULT_COLORS.canvas};width:100%;`);
    canvas.setAttribute('bgcolor', EMAIL_REGION_DEFAULT_COLORS.canvas);

    const canvasRow = document.createElement('tr');
    canvasRow.appendChild(canvasCell);
    canvas.appendChild(canvasRow);
    document.body.appendChild(canvas);
};

const wrapCanvasInnerInCard = (document: Document): void => {
    if (queryEmailRegion(document, 'card')) return;

    const canvas = queryEmailRegion(document, 'canvas');
    if (!canvas) return;

    const host = innerHost(canvas);
    const content = document.createElement('td');
    content.setAttribute('style', 'padding:24px;');
    Array.from(host.childNodes).forEach((node) => content.appendChild(node));

    const card = createPresentationTable(document, '600');
    card.setAttribute(EMAIL_REGION_ATTRIBUTE, 'card');
    card.setAttribute('style', `background-color:${EMAIL_REGION_DEFAULT_COLORS.card};width:600px;`);
    card.setAttribute('bgcolor', EMAIL_REGION_DEFAULT_COLORS.card);

    const headerRow = document.createElement('tr');
    headerRow.appendChild(queryEmailRegion(document, 'header') ?? createHeaderCell(document));
    const contentRow = document.createElement('tr');
    contentRow.appendChild(content);
    card.appendChild(headerRow);
    card.appendChild(contentRow);
    host.appendChild(card);
};

const insertHeaderInCard = (document: Document): void => {
    if (queryEmailRegion(document, 'header')) return;

    const card = queryEmailRegion(document, 'card');
    if (!card || card.tagName !== 'TABLE') return;

    const headerRow = document.createElement('tr');
    headerRow.appendChild(createHeaderCell(document));
    const parent = (card as HTMLTableElement).tBodies[0] ?? card;
    parent.insertBefore(headerRow, parent.firstChild);
};

export const ensureEmailRegions = (document: Document): void => {
    if (EMAIL_REGIONS.every((region) => queryEmailRegion(document, region))) return;

    if (!queryEmailRegion(document, 'canvas') && !queryEmailRegion(document, 'card') && !queryEmailRegion(document, 'header')) {
        wrapBodyInSkeleton(document);
        return;
    }

    wrapExistingInCanvas(document);
    wrapCanvasInnerInCard(document);
    insertHeaderInCard(document);
};

const readRegionColor = (element: HTMLElement | null): string | undefined => {
    if (!element) return undefined;

    const fromStyle = normalizeColor(element.style.backgroundColor || element.style.background);
    if (fromStyle) return fromStyle;

    const bgcolor = element.getAttribute('bgcolor')?.trim();
    if (!bgcolor) return undefined;
    return normalizeColor(bgcolor.startsWith('#') ? bgcolor : `#${bgcolor}`) ?? undefined;
};

export const readEmailRegionColors = (document: Document): Partial<Record<EmailRegion, string>> => {
    const colors: Partial<Record<EmailRegion, string>> = {};
    EMAIL_REGIONS.forEach((region) => {
        const color = readRegionColor(queryEmailRegion(document, region));
        if (color) colors[region] = color;
    });
    return colors;
};

export const applyEmailRegionFill = (document: Document, region: EmailRegion, color: string): void => {
    const fill = color.trim();
    if (!fill) return;

    ensureEmailRegions(document);
    const element = queryEmailRegion(document, region);
    if (!element) return;
    paintBackground(element, fill);
};
